import { useState } from 'react';

import config from '@/config';
import useStateHandler from '@/hooks/useStateHandler'; // Import state handler for temporal popups
import useTranslations from '@/i18n/useTranslations';
import GalleryService from '@/services/gallery.service';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import PublicFormInput from '@/shared-components/inputs/components/PublicFormInput';
import Utils from '@/utils';

import './styles/RepresentativeForm.css';

export default function RepresentativeForm({ fetchData, onEditGalleryAgent }) {
  const t = useTranslations('common.user_settings.gallery_information.representative_form');
  const [formState, setFormState] = useState({
    ...fetchData.gallery_agent,
    agent_birth: Utils.getInputValueFromYearObject(fetchData.gallery_agent.agent_birth)
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Destructure stateHandler to trigger temporal popups
  const { stateHandler } = useStateHandler();

  async function changeRepresentativeInformation(e) {
    e.preventDefault();
    setLoading(true); 
    setError(null); 

    const newGalleryAgent = {
      ...formState,
      agent_birth: Utils.getYearObjectFromInput(formState.agent_birth)
    };

    const { response, data } = await GalleryService.editGalleryAgent(newGalleryAgent);

    if (response.ok) {
      setLoading(false);
      // trigger the temporal popup to display the success message.
      stateHandler.set("temporalPopup", { 
        text: t('success_updated'), // If we add more parts in the form, we'll need to add different ones, for different purposes
      });
      onEditGalleryAgent(data.gallery_agent);
    } else if (response.status === 400) {
      setLoading(false);
      setError({ element: data.error_data.element, error_code: data.error_type });
    }
  }

  return (
    <>
      <form onSubmit={changeRepresentativeInformation} className="settings-representative-form__form">
        <PublicFormInput 
          error={error} 
          placeholder={t('placeholders.full_name')}
          element="gallery_agent.agent_name" 
          margin 
          readOnly={loading}
          value={formState.agent_name}
          onChange={e => setFormState({ ...formState, agent_name: e.target.value })}
        />

        <PublicFormInput 
          error={error} 
          placeholder={t('placeholders.date_of_birth')}
          type="date" 
          element="gallery_agent.agent_birth" 
          margin 
          readOnly={loading}
          value={formState.agent_birth}
          onChange={e => setFormState({ ...formState, agent_birth: e.target.value })}
        />

        <div className="settings-representative-form__form-group">
          <PublicFormInput 
            readOnly={loading} 
            className="settings-representative-form__form-group-margin" 
            error={error} 
            placeholder={t('placeholders.phone_prefix')}
            element="gallery_agent.agent_phone.phone_prefix" 
            type="select" 
            selectOptions={{ options: config.forms.prefix_options, type: "phone_prefix" }} 
            value={formState.agent_phone.phone_prefix}
            onChange={value => setFormState({
              ...formState, 
              agent_phone: {
                ...formState.agent_phone, 
                phone_prefix: value
              }
            })}
          />

          <PublicFormInput 
            readOnly={loading} 
            error={error} 
            placeholder={t('placeholders.phone_number')}
            element="gallery_agent.agent_phone.phone_number"
            value={formState.agent_phone.phone_number}
            onChange={e => setFormState({
              ...formState, 
              agent_phone: {
                ...formState.agent_phone, 
                phone_number: e.target.value
              }
            })}
          />
        </div>

        <ContinueButton loading={loading}>
          {t('buttons.save_changes')}
        </ContinueButton>
      </form>
    </>
  );
}
